import { DFMCheck, DFMCheckStatus, DFMCheckType } from './dfm-check.model';

export type DFMStatusSeverity = 'success' | 'info' | 'warn' | 'danger';

export type DFMVisualizationMode = 'blocking-up' | 'holes-count' | 'property';

type DFMQuantityFormatter = (dfmCheck: DFMCheck, quantity: number) => string;

export interface DFMCheckDefinition {
  label: string;
  icon: string;
  isWarningType: boolean;
  visualizationMode: DFMVisualizationMode | null;
  propertyMap: (materialId: number) => string | null;
  formatQuantity: DFMQuantityFormatter;
  /** Human-readable labels for each key in dfm_check_parameters. */
  parameterLabels: Record<string, string>;
  /** Short description shown in the DFM check panel. */
  description: string;
  /** Short explanation shown in the materials page. */
  tooltip: string;
}

// ---------------------------------------------------------------------------
// Status metadata
// ---------------------------------------------------------------------------

export const DFM_STATUS_INFO: Record<
  DFMCheckStatus,
  { label: string; severity: DFMStatusSeverity }
> = {
  [DFMCheckStatus.PENDING]: { label: 'Pending', severity: 'warn' },
  [DFMCheckStatus.IN_QUEUE]: { label: 'In Queue', severity: 'warn' },
  [DFMCheckStatus.IN_PROGRESS]: { label: 'In Progress', severity: 'info' },
  [DFMCheckStatus.SAVING]: { label: 'Saving', severity: 'info' },
  [DFMCheckStatus.FAILED]: { label: 'Failed', severity: 'danger' },
  [DFMCheckStatus.COMPLETED]: { label: 'Completed', severity: 'success' },
};

// ---------------------------------------------------------------------------
// Quantity formatters
// ---------------------------------------------------------------------------

function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? singular + 's')}`;
}

const formatCount =
  (suffix: string): DFMQuantityFormatter =>
  (_dfmCheck, quantity) =>
    `${quantity.toFixed(0)} ${suffix}`;

const formatArea: DFMQuantityFormatter = (_dfmCheck, quantity) => {
  if (quantity > 1_000_000) {
    return `${(quantity / 1_000_000).toFixed(2)} m²`;
  }
  if (quantity > 100) {
    return `${(quantity / 100).toFixed(2)} cm²`;
  }
  return `${quantity.toFixed(2)} mm²`;
};

const formatVolume: DFMQuantityFormatter = (_dfmCheck, quantity) => {
  if (quantity > 1_000_000_000) {
    return `${(quantity / 1_000_000_000).toFixed(2)} m³`;
  }
  if (quantity > 1_000) {
    return `${(quantity / 1_000).toFixed(2)} cm³`;
  }
  return `${quantity.toFixed(2)} mm³`;
};

const formatBlockingUp: DFMQuantityFormatter = (dfmCheck) => {
  if (!dfmCheck.blocking_up) {
    return 'No blocking up data';
  }

  const details: string[] = [];
  const { blocking_up, num_sheets_needed } = dfmCheck;

  if (blocking_up && num_sheets_needed) {
    const blocks = pluralize(blocking_up.length, 'block');
    const sheets = pluralize(num_sheets_needed, 'sheet');
    details.push(`${blocks} needed, requiring ${sheets} of material`);
  }

  return details.length > 0 ? details.join(', ') : 'No details';
};

// ---------------------------------------------------------------------------
// Check-type definitions (single source of truth per check type)
// ---------------------------------------------------------------------------

export const DFM_CHECK_DEFINITIONS: Record<DFMCheckType, DFMCheckDefinition> = {
  [DFMCheckType.HOLES_COUNT]: {
    label: 'Holes Count',
    icon: 'pi pi-circle',
    isWarningType: false,
    visualizationMode: 'holes-count',
    propertyMap: () => 'holeDiameters',
    formatQuantity: formatCount('holes'),
    parameterLabels: {},
    description:
      'Holes identified in the part are highlighted in blue for your information.',
    tooltip: 'Identifies all holes in the part.',
  },
  [DFMCheckType.UNMACHINABLE_HOLES]: {
    label: 'Unmachinable Holes',
    icon: 'pi pi-ban',
    isWarningType: true,
    visualizationMode: 'property',
    propertyMap: (materialId) => `holeIsUnmachinable_${materialId}`,
    formatQuantity: formatCount('unmachinable holes'),
    parameterLabels: {
      drill_head_radius_mm: 'Drill Head Radius (mm)',
      drill_bit_length_to_diameter_ratio_blind: 'L/D Ratio – Blind Holes',
      drill_bit_length_to_diameter_ratio_through: 'L/D Ratio – Through Holes',
    },
    description:
      'Holes where the drill bit cannot reach due to depth or diameter constraints. Unmachinable holes are highlighted in red.',
    tooltip:
      'Checks whether holes exceed the machining limits defined by drill size and depth-to-diameter ratios.',
  },
  [DFMCheckType.THIN_WALLS]: {
    label: 'Thin Walls',
    icon: 'pi pi-arrows-h',
    isWarningType: true,
    visualizationMode: 'property',
    propertyMap: (materialId) => `thinWalls_${materialId}`,
    formatQuantity: formatArea,
    parameterLabels: {
      min_thickness_mm: 'Min Thickness (mm)',
      max_thickness_mm: 'Max Thickness (mm)',
    },
    description:
      'Thin walls may be hard to manufacture and could break during the moulding process. Thin walls are highlighted in red.',
    tooltip:
      'Identifies walls that fall below the minimum manufacturable thickness for this material.',
  },
  [DFMCheckType.NARROW_GAPS]: {
    label: 'Narrow Gaps',
    icon: 'pi pi-arrow-down-left-and-arrow-up-right-to-center',
    isWarningType: true,
    visualizationMode: 'property',
    propertyMap: (materialId) => `narrowGaps_${materialId}`,
    formatQuantity: formatArea,
    parameterLabels: {
      angle_tolerance_degrees: 'Angle Tolerance (°)',
    },
    description:
      'Narrow gaps may only be machinable to a limited depth. Narrow gaps are highlighted in red.',
    tooltip:
      'Identifies gaps and slots that may be too narrow to manufacture reliably.',
  },
  [DFMCheckType.INTERNAL_VOLUME]: {
    label: 'Internal Volume',
    icon: 'pi pi-box',
    isWarningType: true,
    visualizationMode: 'property',
    propertyMap: (materialId) => `internalVolume_${materialId}`,
    formatQuantity: formatVolume,
    parameterLabels: {},
    description:
      'Internal volume (enclosed cavities) may be impossible to machine. Internal volume is highlighted in red when opacity is toggled on.',
    tooltip:
      'Identifies enclosed internal cavities that may be difficult or impossible to manufacture.',
  },
  [DFMCheckType.BLOCKING_UP]: {
    label: 'Blocking Up',
    icon: 'pi pi-th-large',
    isWarningType: false,
    visualizationMode: 'blocking-up',
    propertyMap: () => null,
    formatQuantity: formatBlockingUp,
    parameterLabels: {
      sheet_size_x_mm: 'Sheet Length (mm)',
      sheet_size_y_mm: 'Sheet Width (mm)',
      sheet_size_z_mm: 'Sheet Thickness (mm)',
    },
    description:
      'Shows how material sheets are assembled prior to milling and how many sheets are required.',
    tooltip:
      'Calculates how the part can be built from stacked material sheets and estimates the number of sheets required.',
  },
};
